import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

// Entity types are shown as stored. src/utils/entityLabels (which renders Syngenta's
// "Product" as "Brand") lands with the entity-type-labels branch; wiring typeLabel()
// through here is a one-line follow-up once that merges, and keeping this component
// off it means this branch can merge independently.

/**
 * Entity group membership admin — put entities into a curated category, take them out.
 *
 * Groups are a second classification axis beside entity_type: 'Product' says what a
 * thing IS, a group says which category it belongs to for ranking and filtering. Without
 * one, all 613 of Syngenta's products compete in a single Market Watch column capped at
 * 8, and anything past rank 8 reads as missing data.
 *
 * Until now every membership row came from tools/seed_entity_groups.py reading a CSV, so
 * pests are 100% classified (the library already carried a biological type per row) and
 * products only 7%. This is where a person classifies the rest.
 *
 * Deliberately membership-only. Creating a group means minting an entity plus usually a
 * virtual root type — a taxonomy decision the pipeline's is_virtual guard depends on —
 * and deleting one orphans the Market Watch columns and saved views that name it. Both
 * stay out-of-band (fieldforce_dr_roof/tools/create_entity_group.py). Nothing here is
 * specific to any one group: it lists whatever groups the DB has.
 */

const S = {
  section: { marginBottom: 0 },   // sits inside CollapsibleSection, which owns the spacing
  h3: { marginBottom: '4px', fontSize: '18px', color: '#1a1a1a' },
  hint: { fontSize: '13px', color: '#6c757d', marginBottom: '16px' },
  bar: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' },
  input: { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '6px', fontSize: '13px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', background: '#fff' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e9ecef', color: '#6c757d',
        fontSize: '11px', textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  btn: { padding: '5px 12px', borderRadius: '6px', border: '1px solid #ced4da', background: '#fff',
         cursor: 'pointer', fontSize: '12px', fontWeight: 600 },
  btnAdd: { padding: '5px 12px', borderRadius: '6px', border: '1px solid #a3e0bd', background: '#e6f7ed',
            cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#1a7f4b' },
  panel: { padding: '14px', background: '#f8f9fa', border: '1px solid #e9ecef',
           borderRadius: '8px', marginBottom: '14px' },
  ok: { padding: '10px 12px', background: '#e6f7ed', border: '1px solid #a3e0bd',
        borderRadius: '6px', fontSize: '13px', marginBottom: '12px', color: '#166534' },
  err: { padding: '10px 12px', background: '#fdecea', border: '1px solid #f5c2c0',
         borderRadius: '6px', fontSize: '13px', marginBottom: '12px', color: '#8a1c17' },
  muted: { color: '#868e96', fontSize: '11px' },
  cols: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '20px', alignItems: 'start' },
  colHead: { fontSize: '13px', fontWeight: 700, color: '#495057', marginBottom: '8px' },
  scroll: { maxHeight: '340px', overflowY: 'auto', border: '1px solid #e9ecef', borderRadius: '8px' },
  chip: { display: 'inline-block', padding: '1px 7px', borderRadius: '10px', background: '#eef2ff',
          color: '#3730a3', fontSize: '10px', fontWeight: 700, marginLeft: '6px' },
};

function GroupMembership({ org, currentUser }) {
  const [groups, setGroups] = useState([]);
  const [supported, setSupported] = useState(true);
  const [groupId, setGroupId] = useState('');
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const [candidates, setCandidates] = useState([]);
  const [candType, setCandType] = useState('');
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState('');

  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // `org` is only passed by the Dachido cross-org admin panel. From FieldHQ it is
  // omitted, and the backend scopes every query to the caller's own organisation.
  const params = useCallback((extra = {}) => (org ? { pipeline: org, ...extra } : extra), [org]);

  const loadGroups = useCallback(() => {
    axios.get('/api/dachido/entity-groups', { params: params() })
      .then(res => {
        setSupported(res.data.groups_supported !== false);
        setGroups(res.data.groups || []);
      })
      .catch(err => setError(err.response?.data?.error || err.message));
  }, [params]);

  const loadMembers = useCallback(() => {
    if (!groupId) { setMembers([]); return; }
    setMembersLoading(true);
    axios.get(`/api/dachido/entity-groups/${groupId}/members`, { params: params() })
      .then(res => setMembers(res.data.members || []))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setMembersLoading(false));
  }, [groupId, params]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Candidates come from the merge picker's own endpoint rather than a new one — same
  // question ("which entities does this org have, by type and name"), same shape, and it
  // already returns conversation counts.
  useEffect(() => {
    if (!groupId) { setCandidates([]); return; }
    axios.get('/api/dachido/entities', { params: params({ type: candType, q, limit: 200 }) })
      .then(res => {
        setCandidates(res.data.entities || []);
        if (res.data.entity_types?.length) setTypes(res.data.entity_types);
      })
      .catch(() => setCandidates([]));
  }, [groupId, candType, q, params]);

  const selected = groups.find(g => String(g.entity_id) === String(groupId));
  const memberIds = useMemo(() => new Set(members.map(m => m.entity_id)), [members]);

  // The group itself is never offerable as its own member, and neither is anything
  // already in it — the backend refuses both, but a button that always errors is worse
  // than one that isn't there.
  const offerable = candidates.filter(
    c => c.entity_id !== Number(groupId) && !memberIds.has(c.entity_id));

  const addMember = (entity) => {
    setBusyId(entity.entity_id);
    setError(null);
    axios.post(`/api/dachido/entity-groups/${groupId}/members`, {
      member_id: entity.entity_id,
      created_by: currentUser?.username || currentUser?.email || 'admin',
    }, { params: params() })
      .then(res => {
        setNotice(res.data.already_member
          ? `"${res.data.member_name}" was already in ${res.data.group_name}`
          : `Added "${res.data.member_name}" to ${res.data.group_name}`);
        loadMembers();
        loadGroups();
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setBusyId(null));
  };

  const removeMember = (member) => {
    setBusyId(member.entity_id);
    setError(null);
    axios.delete(`/api/dachido/entity-groups/${groupId}/members/${member.entity_id}`,
                 { params: params() })
      .then(res => {
        setNotice(`Removed "${res.data.member_name}" from ${res.data.group_name}`);
        loadMembers();
        loadGroups();
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setBusyId(null));
  };

  if (!supported) {
    return (
      <section style={S.section}>
        <div style={S.hint}>
          This organisation's database has no entity group layer yet
          (migration 026 has not been applied), so there is nothing to curate.
        </div>
      </section>
    );
  }

  // Groups are listed under their root type ('Ingredient Type', 'Pest Type', ...), which
  // is how the taxonomy is actually organised — a flat list of 13 names does not say that
  // Fungicide classifies products and Insect classifies pests.
  const byRoot = groups.reduce((acc, g) => {
    (acc[g.entity_type] = acc[g.entity_type] || []).push(g);
    return acc;
  }, {});

  return (
    <section style={S.section}>
      <div style={S.hint}>
        Groups classify entities for ranking and filtering, beside their entity type —
        picking one on Field Intelligence pools everything inside it. Membership is
        curated here; the pipeline never writes it.
      </div>

      {error && <div style={S.err}>{error}</div>}
      {notice && <div style={S.ok}>{notice}</div>}

      <div style={S.panel}>
        <div style={S.bar}>
          <label style={{ fontSize: '13px', fontWeight: 600 }}>Group</label>
          <select
            style={{ ...S.input, minWidth: '260px' }}
            value={groupId}
            onChange={e => { setGroupId(e.target.value); setNotice(null); setError(null); }}
          >
            <option value="">Select a group…</option>
            {Object.entries(byRoot).map(([root, gs]) => (
              <optgroup key={root} label={root}>
                {gs.map(g => (
                  <option key={g.entity_id} value={g.entity_id}>
                    {g.entity_name} ({g.member_count})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {selected && (
            <span style={S.muted}>
              {selected.member_count} member{selected.member_count === 1 ? '' : 's'}
              {' · root type '}{selected.entity_type}
            </span>
          )}
        </div>
      </div>

      {!groupId ? (
        <div style={S.hint}>Pick a group to see and edit what is in it.</div>
      ) : (
        <div style={S.cols}>
          <div>
            <div style={S.colHead}>
              In {selected?.entity_name} {membersLoading && <span style={S.muted}>loading…</span>}
            </div>
            <div style={S.scroll}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Entity</th>
                    <th style={{ ...S.th, ...S.num }}>Convs</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 && !membersLoading && (
                    <tr><td style={S.td} colSpan={3}>
                      <span style={S.muted}>Nothing in this group yet.</span>
                    </td></tr>
                  )}
                  {members.map(m => (
                    <tr key={m.entity_id}>
                      <td style={S.td}>
                        {m.entity_name}
                        {m.is_group ? <span style={S.chip}>GROUP</span> : null}
                        <span style={{ ...S.muted, display: 'block', marginTop: '2px' }}>
                          {m.entity_type}
                          {m.source ? ` · ${m.source}` : ''}
                          {m.created_by ? ` · ${m.created_by}` : ''}
                        </span>
                      </td>
                      <td style={{ ...S.td, ...S.num }}>{m.conversations ?? 0}</td>
                      <td style={S.td}>
                        <button
                          style={{ ...S.btn, opacity: busyId === m.entity_id ? 0.5 : 1 }}
                          disabled={busyId === m.entity_id}
                          onClick={() => removeMember(m)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <div style={S.colHead}>Add an entity</div>
            <div style={S.bar}>
              <select style={S.input} value={candType} onChange={e => setCandType(e.target.value)}>
                <option value="">All types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input
                style={{ ...S.input, flex: 1, minWidth: '140px' }}
                placeholder="Search by name…"
                value={q}
                onChange={e => setQ(e.target.value)}
              />
            </div>
            <div style={S.scroll}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Entity</th>
                    <th style={{ ...S.th, ...S.num }}>Convs</th>
                    <th style={S.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {offerable.length === 0 && (
                    <tr><td style={S.td} colSpan={3}>
                      <span style={S.muted}>
                        No entities match — everything found is already in this group.
                      </span>
                    </td></tr>
                  )}
                  {offerable.map(c => (
                    <tr key={c.entity_id}>
                      <td style={S.td}>
                        {c.entity_name}
                        <span style={{ ...S.muted, display: 'block', marginTop: '2px' }}>
                          {c.entity_type}
                        </span>
                      </td>
                      <td style={{ ...S.td, ...S.num }}>{c.conversations ?? 0}</td>
                      <td style={S.td}>
                        <button
                          style={{ ...S.btnAdd, opacity: busyId === c.entity_id ? 0.5 : 1 }}
                          disabled={busyId === c.entity_id}
                          onClick={() => addMember(c)}
                        >
                          Add
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default GroupMembership;
